import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { FiX, FiHome, FiMapPin, FiDollarSign, FiSquare, FiCalendar, FiCheck, FiImage, FiPlus } from 'react-icons/fi';
import { theme } from '@theme';
import { Housing } from '@/models';
import { HousingsServices } from '@/services/HousingServices';

interface HousingFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: () => void; // Called after successful submission to refresh parent list
  initialData?: Housing;
  mode: 'add' | 'edit';
}

// Styled Components
const ModalOverlay = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: ${props => props.isOpen ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: ${theme.spacing.lg};
`;

const ModalContainer = styled.div`
  background: white;
  border-radius: ${theme.borderRadius.lg};
  box-shadow: ${theme.shadows.lg};
  width: 100%;
  max-width: 900px;
  max-height: 90vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const ModalHeader = styled.div`
  padding: ${theme.spacing.xl};
  border-bottom: 1px solid ${theme.colors.surface};
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ModalTitle = styled.h2`
  font-size: ${theme.typography.sizes.xl};
  font-weight: ${theme.typography.weights.bold};
  color: ${theme.colors.text};
  margin: 0;
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};

  svg {
    color: ${theme.colors.primary};
  }
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  color: ${theme.colors.text}60;
  cursor: pointer;
  padding: ${theme.spacing.sm};
  border-radius: ${theme.borderRadius.md};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all ${theme.transitions.fast};

  &:hover {
    background: ${theme.colors.surface};
    color: ${theme.colors.text};
  }

  svg {
    font-size: 24px;
  }
`;

const ModalBody = styled.div`
  padding: ${theme.spacing.xl};
  overflow-y: auto;
  overflow-x: hidden;
  flex: 1;
  max-height: calc(90vh - 160px);

  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: ${theme.colors.surface};
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: ${theme.colors.text}40;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: ${theme.colors.text}60;
  }
`;

const FormSection = styled.div`
  margin-bottom: ${theme.spacing.xl};

  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionTitle = styled.h3`
  font-size: ${theme.typography.sizes.lg};
  font-weight: ${theme.typography.weights.semibold};
  color: ${theme.colors.text};
  margin: 0 0 ${theme.spacing.md} 0;
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};

  svg {
    color: ${theme.colors.primary};
  }
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${theme.spacing.md};

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.xs};
`;

const Label = styled.label`
  font-size: ${theme.typography.sizes.sm};
  font-weight: ${theme.typography.weights.semibold};
  color: ${theme.colors.text};
`;

const Input = styled.input`
  padding: ${theme.spacing.md};
  border: 1px solid ${theme.colors.surface};
  border-radius: ${theme.borderRadius.md};
  font-family: ${theme.typography.fontFamily};
  font-size: ${theme.typography.sizes.sm};
  color: ${theme.colors.text};
  transition: all ${theme.transitions.fast};

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
    box-shadow: 0 0 0 3px ${theme.colors.primary}20;
  }

  &:disabled {
    background: ${theme.colors.surface}50;
    cursor: not-allowed;
  }
`;

const Select = styled.select`
  padding: ${theme.spacing.md};
  border: 1px solid ${theme.colors.surface};
  border-radius: ${theme.borderRadius.md};
  font-family: ${theme.typography.fontFamily};
  font-size: ${theme.typography.sizes.sm};
  color: ${theme.colors.text};
  background: white;
  cursor: pointer;
  transition: all ${theme.transitions.fast};

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
    box-shadow: 0 0 0 3px ${theme.colors.primary}20;
  }
`;

const TextArea = styled.textarea`
  padding: ${theme.spacing.md};
  border: 1px solid ${theme.colors.surface};
  border-radius: ${theme.borderRadius.md};
  font-family: ${theme.typography.fontFamily};
  font-size: ${theme.typography.sizes.sm};
  color: ${theme.colors.text};
  min-height: 100px;
  resize: vertical;
  transition: all ${theme.transitions.fast};

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
    box-shadow: 0 0 0 3px ${theme.colors.primary}20;
  }
`;

const ChipContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${theme.spacing.sm};
  margin-top: ${theme.spacing.sm};
`;

const Chip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing.xs};
  padding: ${theme.spacing.xs} ${theme.spacing.md};
  background: ${theme.colors.primary}20;
  color: ${theme.colors.primary};
  border-radius: ${theme.borderRadius.full};
  font-size: ${theme.typography.sizes.sm};
`;

const ChipRemove = styled.button`
  background: transparent;
  border: none;
  color: ${theme.colors.primary};
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all ${theme.transitions.fast};

  &:hover {
    color: ${theme.colors.error};
  }
`;

const AddButton = styled.button`
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  background: ${theme.colors.surface};
  color: ${theme.colors.text};
  border: 1px dashed ${theme.colors.text}40;
  border-radius: ${theme.borderRadius.md};
  font-family: ${theme.typography.fontFamily};
  font-size: ${theme.typography.sizes.sm};
  cursor: pointer;
  transition: all ${theme.transitions.fast};
  margin-top: ${theme.spacing.sm};

  &:hover {
    background: ${theme.colors.primary}10;
    border-color: ${theme.colors.primary};
    color: ${theme.colors.primary};
  }
`;

const ModalFooter = styled.div`
  padding: ${theme.spacing.xl};
  border-top: 1px solid ${theme.colors.surface};
  display: flex;
  justify-content: flex-end;
  gap: ${theme.spacing.md};
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' }>`
  padding: ${theme.spacing.md} ${theme.spacing.xl};
  background: ${props => props.variant === 'primary' ? theme.colors.primary : 'transparent'};
  color: ${props => props.variant === 'primary' ? 'white' : theme.colors.text};
  border: 1px solid ${props => props.variant === 'primary' ? theme.colors.primary : theme.colors.surface};
  border-radius: ${theme.borderRadius.md};
  font-family: ${theme.typography.fontFamily};
  font-size: ${theme.typography.sizes.sm};
  font-weight: ${theme.typography.weights.semibold};
  cursor: pointer;
  transition: all ${theme.transitions.fast};

  &:hover {
    background: ${props => props.variant === 'primary' ? theme.colors.primary + 'DD' : theme.colors.surface};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

// Image upload components
const ImageUploadContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.md};
`;

const ImagePreviewBox = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 16/9;
  border: 2px dashed ${theme.colors.surface};
  border-radius: ${theme.borderRadius.md};
  overflow: hidden;
  background: ${theme.colors.surface}50;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all ${theme.transitions.fast};

  &:hover {
    border-color: ${theme.colors.primary};
    background: ${theme.colors.primary}05;
  }
`;

const ImagePreview = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const ImagePlaceholder = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${theme.spacing.sm};
  color: ${theme.colors.text}60;
  font-size: ${theme.typography.sizes.sm};

  svg {
    font-size: 48px;
  }
`;

const RemoveImageButton = styled.button`
  position: absolute;
  top: ${theme.spacing.sm};
  right: ${theme.spacing.sm};
  padding: ${theme.spacing.xs};
  background: white;
  border: none;
  border-radius: ${theme.borderRadius.sm};
  color: ${theme.colors.error};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: ${theme.shadows.sm};
  transition: all ${theme.transitions.fast};

  &:hover {
    background: ${theme.colors.error}10;
    transform: scale(1.1);
  }

  svg {
    font-size: 18px;
  }
`;

const HiddenFileInput = styled.input`
  display: none;
`;

const PhotoGalleryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: ${theme.spacing.md};
`;

const PhotoThumbnail = styled.div`
  position: relative;
  aspect-ratio: 4/3;
  border-radius: ${theme.borderRadius.md};
  overflow: hidden;
  background: ${theme.colors.surface};
  cursor: pointer;
  transition: all ${theme.transitions.fast};

  &:hover {
    transform: scale(1.02);
    box-shadow: ${theme.shadows.md};
  }
`;

const AddPhotoBox = styled.div`
  aspect-ratio: 4/3;
  border: 2px dashed ${theme.colors.surface};
  border-radius: ${theme.borderRadius.md};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing.sm};
  color: ${theme.colors.text}60;
  cursor: pointer;
  transition: all ${theme.transitions.fast};
  font-size: ${theme.typography.sizes.sm};

  &:hover {
    border-color: ${theme.colors.primary};
    color: ${theme.colors.primary};
    background: ${theme.colors.primary}05;
  }

  svg {
    font-size: 32px;
  }
`;

export const HousingFormModal: React.FC<HousingFormModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  initialData,
  mode
}) => {
  const [formData, setFormData] = useState<Partial<Housing>>({
    reference: '',
    type: 'T1',
    address: '',
    city: '',
    postal_code: '',
    surface: 0,
    rooms: 1,
    bedrooms: 1,
    bathrooms: 1,
    min_revenue: 0,
    rent: 0,
    charges: 0,
    deposit: 0,
    available_date: '',
    status: 'available',
    description: '',
    features: [],
    conditions: [],
    energy_class: 'C',
    ghg_class: 'C'
  });

  const [newFeature, setNewFeature] = useState('');
  const [newCondition, setNewCondition] = useState('');
  const [mainPhotoPreview, setMainPhotoPreview] = useState<string | null>(null);
  const [photosPreview, setPhotosPreview] = useState<string[]>([]);
  const [mainPhotoFile, setMainPhotoFile] = useState<File | null>(null);
  const [photoFiles, setPhotoFiles] = useState<File[]>([]);
  const [submitting, setSubmitting] = useState(false);

  const mainPhotoInputRef = useRef<HTMLInputElement>(null);
  const photosInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (initialData) {
      setFormData(initialData);
      // Charger les previews d'images si elles existent
      if (initialData.main_photo && typeof initialData.main_photo === 'string') {
        setMainPhotoPreview(initialData.main_photo);
      }
      if (initialData.photos && Array.isArray(initialData.photos)) {
        const previews = initialData.photos
          .filter((p): p is string => typeof p === 'string')
          .map(p => p);
        setPhotosPreview(previews);
      }
    } else {
      // Reset form for add mode
      setFormData({
        reference: '',
        type: 'T1',
        address: '',
        city: '',
        postal_code: '',
        surface: 0,
        rooms: 1,
        bedrooms: 1,
        bathrooms: 1,
        rent: 0,
        charges: 0,
        deposit: 0,
        available_date: '',
        status: 'available',
        description: '',
        features: [],
        conditions: [],
        energy_class: 'C',
        ghg_class: 'C'
      });
      setMainPhotoPreview(null);
      setPhotosPreview([]);
      setMainPhotoFile(null);
      setPhotoFiles([]);
    }
  }, [initialData, isOpen]);

  const handleChange = (field: keyof Housing, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Gestion de l'image principale
  const handleMainPhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setMainPhotoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setMainPhotoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveMainPhoto = () => {
    setMainPhotoFile(null);
    setMainPhotoPreview(null);
    if (mainPhotoInputRef.current) {
      mainPhotoInputRef.current.value = '';
    }
  };

  // Gestion de la galerie de photos
  const handlePhotosChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) {
      setPhotoFiles(prev => [...prev, ...files]);

      files.forEach(file => {
        const reader = new FileReader();
        reader.onloadend = () => {
          setPhotosPreview(prev => [...prev, reader.result as string]);
        };
        reader.readAsDataURL(file);
      });
    }
  };

  const handleRemovePhoto = (index: number) => {
    setPhotoFiles(prev => prev.filter((_, i) => i !== index));
    setPhotosPreview(prev => prev.filter((_, i) => i !== index));
  };

  const handleAddFeature = () => {
    if (newFeature.trim()) {
      setFormData(prev => ({
        ...prev,
        features: [...(prev.features || []), newFeature.trim()]
      }));
      setNewFeature('');
    }
  };

  const handleRemoveFeature = (index: number) => {
    setFormData(prev => ({
      ...prev,
      features: (prev.features || []).filter((_, i) => i !== index)
    }));
  };

  const handleAddCondition = () => {
    if (newCondition.trim()) {
      setFormData(prev => ({
        ...prev,
        conditions: [...(prev.conditions || []), newCondition.trim()]
      }));
      setNewCondition('');
    }
  };

  const handleRemoveCondition = (index: number) => {
    setFormData(prev => ({
      ...prev,
      conditions: (prev.conditions || []).filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      // Créer une copie des données et supprimer les champs read-only/calculés
      const submitData: any = { ...formData };

      // Supprimer les champs qui ne doivent pas être envoyés
      delete submitData.housing_photos;
      delete submitData.main_photo;
      delete submitData.photos_count;
      delete submitData.created_at;
      delete submitData.updated_at;

      // Convert array fields to proper format if needed
      if (formData.features && !Array.isArray(formData.features)) {
        submitData.features = [];
      }
      if (formData.conditions && !Array.isArray(formData.conditions)) {
        submitData.conditions = [];
      }

      if (mode === 'add') {
        // Create new housing
        await HousingsServices.create(submitData);
        alert('Logement ajouté avec succès!');
      } else {
        // Update existing housing
        if (formData.id) {
          await HousingsServices.update(formData.id, submitData);
          alert('Logement modifié avec succès!');
        }
      }

      onSubmit(); // Notify parent to refresh
      onClose();
    } catch (err: any) {
      alert(`Erreur: ${err.message || 'Une erreur est survenue'}`);
      console.error('Error submitting housing:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <ModalOverlay isOpen={isOpen} onClick={handleOverlayClick}>
      <ModalContainer>
        <ModalHeader>
          <ModalTitle>
            <FiHome />
            {mode === 'add' ? 'Ajouter un logement' : 'Modifier le logement'}
          </ModalTitle>
          <CloseButton onClick={onClose}>
            <FiX />
          </CloseButton>
        </ModalHeader>

        <form onSubmit={handleSubmit}>
          <ModalBody>
            {/* Informations Générales */}
            <FormSection>
              <SectionTitle>
                <FiHome /> Informations Générales
              </SectionTitle>

              <FormGrid>
                <FormGroup>
                  <Label>Référence *</Label>
                  <Input
                    type="text"
                    value={formData.reference || ''}
                    onChange={(e) => handleChange('reference', e.target.value)}
                    placeholder="LOG-2024-001"
                    required
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Type *</Label>
                  <Select
                    value={formData.type || 'T1'}
                    onChange={(e) => handleChange('type', e.target.value)}
                    required
                  >
                    <option value="T1">T1</option>
                    <option value="T2">T2</option>
                    <option value="T3">T3</option>
                    <option value="T4">T4</option>
                    <option value="T5">T5</option>
                  </Select>
                </FormGroup>

                <FormGroup>
                  <Label>Surface (m²) *</Label>
                  <Input
                    type="number"
                    min="0"
                    value={formData.surface || ''}
                    onChange={(e) => handleChange('surface', parseInt(e.target.value))}
                    placeholder="85"
                    required
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Nombre de pièces *</Label>
                  <Input
                    type="number"
                    min="1"
                    value={formData.rooms || ''}
                    onChange={(e) => handleChange('rooms', parseInt(e.target.value))}
                    required
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Chambres *</Label>
                  <Input
                    type="number"
                    min="0"
                    value={formData.bedrooms || ''}
                    onChange={(e) => handleChange('bedrooms', parseInt(e.target.value))}
                    required
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Salles de bain *</Label>
                  <Input
                    type="number"
                    min="1"
                    value={formData.bathrooms || ''}
                    onChange={(e) => handleChange('bathrooms', parseInt(e.target.value))}
                    required
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Statut *</Label>
                  <Select
                    value={formData.status || 'available'}
                    onChange={(e) => handleChange('status', e.target.value)}
                    required
                  >
                    <option value="available">Disponible</option>
                    <option value="attributed">Attribué</option>
                    <option value="maintenance">Maintenance</option>
                  </Select>
                </FormGroup>

                <FormGroup>
                  <Label>Date de disponibilité *</Label>
                  <Input
                    type="date"
                    value={formData.available_date || ''}
                    onChange={(e) => handleChange('available_date', e.target.value)}
                    required
                  />
                </FormGroup>
              </FormGrid>

              <FormGroup style={{ marginTop: theme.spacing.md }}>
                <Label>Description</Label>
                <TextArea
                  value={formData.description || ''}
                  onChange={(e) => handleChange('description', e.target.value)}
                  placeholder="Décrivez le logement..."
                />
              </FormGroup>
            </FormSection>

            {/* Localisation */}
            <FormSection>
              <SectionTitle>
                <FiMapPin /> Localisation
              </SectionTitle>

              <FormGroup>
                <Label>Adresse *</Label>
                <Input
                  type="text"
                  value={formData.address || ''}
                  onChange={(e) => handleChange('address', e.target.value)}
                  placeholder="15 Avenue de la République"
                  required
                />
              </FormGroup>

              <FormGrid>
                <FormGroup>
                  <Label>Ville *</Label>
                  <Input
                    type="text"
                    value={formData.city || ''}
                    onChange={(e) => handleChange('city', e.target.value)}
                    placeholder="Paris"
                    required
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Code postal *</Label>
                  <Input
                    type="text"
                    value={formData.postal_code || ''}
                    onChange={(e) => handleChange('postal_code', e.target.value)}
                    placeholder="75001"
                    required
                  />
                </FormGroup>
              </FormGrid>
            </FormSection>

            {/* Photos */}
            {/* <FormSection> */}
              {/* <SectionTitle>
                <FiImage /> Photos du Logement
              </SectionTitle> */}

              {/* Image Principale */}
              {/* <FormGroup>
                <Label>Image Principale *</Label>
                <HiddenFileInput
                  ref={mainPhotoInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleMainPhotoChange}
                />
                <ImagePreviewBox onClick={() => mainPhotoInputRef.current?.click()}>
                  {mainPhotoPreview ? (
                    <>
                      <ImagePreview src={mainPhotoPreview} alt="Photo principale" />
                      <RemoveImageButton
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveMainPhoto();
                        }}
                      >
                        <FiX />
                      </RemoveImageButton>
                    </>
                  ) : (
                    <ImagePlaceholder>
                      <FiImage />
                      <span>Cliquez pour ajouter une image principale</span>
                      <span style={{ fontSize: '12px', color: theme.colors.text + '40' }}>
                        (Format recommandé: 16:9, max 5MB)
                      </span>
                    </ImagePlaceholder>
                  )}
                </ImagePreviewBox>
              </FormGroup> */}

              {/* Galerie de photos */}
              {/* <FormGroup>
                <Label>Galerie de Photos</Label>
                <HiddenFileInput
                  ref={photosInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handlePhotosChange}
                />
                <PhotoGalleryGrid>
                  {photosPreview.map((preview, index) => (
                    <PhotoThumbnail key={index}>
                      <ImagePreview src={preview} alt={`Photo ${index + 1}`} />
                      <RemoveImageButton
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemovePhoto(index);
                        }}
                      >
                        <FiX />
                      </RemoveImageButton>
                    </PhotoThumbnail>
                  ))}
                  <AddPhotoBox onClick={() => photosInputRef.current?.click()}>
                    <FiPlus />
                    <span>Ajouter des photos</span>
                  </AddPhotoBox>
                </PhotoGalleryGrid>
              </FormGroup> */}
            {/* </FormSection> */}

            {/* Informations Financières */}
            <FormSection>
              <SectionTitle>
                <FiDollarSign /> Informations Financières
              </SectionTitle>

              <FormGrid>
                <FormGroup>
                  <Label>Loyer mensuel (FCFA) *</Label>
                  <Input
                    type="number"
                    min="0"
                    value={formData.rent || ''}
                    onChange={(e) => handleChange('rent', parseInt(e.target.value))}
                    placeholder="850"
                    required
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Charges (FCFA) *</Label>
                  <Input
                    type="number"
                    min="0"
                    value={formData.charges || ''}
                    onChange={(e) => handleChange('charges', parseInt(e.target.value))}
                    placeholder="120"
                    required
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Dépôt de garantie (FCFA) *</Label>
                  <Input
                    type="number"
                    min="0"
                    value={formData.deposit || ''}
                    onChange={(e) => handleChange('deposit', parseInt(e.target.value))}
                    placeholder="1700"
                    required
                  />
                </FormGroup>
              </FormGrid>
            </FormSection>

            {/* Performance Énergétique */}
            <FormSection>
              <SectionTitle>
                🌍 Performance Énergétique
              </SectionTitle>

              <FormGrid>
                <FormGroup>
                  <Label>Classe Énergétique (DPE)</Label>
                  <Select
                    value={formData.energy_class || 'C'}
                    onChange={(e) => handleChange('energy_class', e.target.value)}
                  >
                    <option value="A">A</option>
                    <option value="B">B</option>
                    <option value="C">C</option>
                    <option value="D">D</option>
                    <option value="E">E</option>
                    <option value="F">F</option>
                    <option value="G">G</option>
                  </Select>
                </FormGroup>

                <FormGroup>
                  <Label>Émissions GES</Label>
                  <Select
                    value={formData.ghg_class || 'C'}
                    onChange={(e) => handleChange('ghg_class', e.target.value)}
                  >
                    <option value="A">A</option>
                    <option value="B">B</option>
                    <option value="C">C</option>
                    <option value="D">D</option>
                    <option value="E">E</option>
                    <option value="F">F</option>
                    <option value="G">G</option>
                  </Select>
                </FormGroup>
              </FormGrid>
            </FormSection>

            {/* Équipements */}
            <FormSection>
              <SectionTitle>
                <FiCheck /> Équipements et Prestations
              </SectionTitle>

              <ChipContainer>
                {(formData.features || []).map((feature, index) => (
                  <Chip key={index}>
                    {feature}
                    <ChipRemove type="button" onClick={() => handleRemoveFeature(index)}>
                      <FiX />
                    </ChipRemove>
                  </Chip>
                ))}
              </ChipContainer>

              <div style={{ display: 'flex', gap: theme.spacing.sm, marginTop: theme.spacing.sm }}>
                <Input
                  type="text"
                  value={newFeature}
                  onChange={(e) => setNewFeature(e.target.value)}
                  placeholder="Ex: Balcon, Parking..."
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddFeature())}
                />
                <AddButton type="button" onClick={handleAddFeature}>
                  Ajouter
                </AddButton>
              </div>
            </FormSection>

            {/* Conditions */}
            <FormSection>
              <SectionTitle>
                📋 Conditions de Location
              </SectionTitle>
               <FormGrid>
                <FormGroup>
                  <Label>Revenue Minimum (FCFA)*</Label>
                  <Input
                    type="number"
                    min="0"
                    value={formData.min_revenue || ''}
                    onChange={(e) => handleChange('min_revenue', parseInt(e.target.value))}
                    placeholder="850"
                    required
                  />
                </FormGroup>

                
              </FormGrid>
                
              <ChipContainer>
                {(formData.conditions || []).map((condition, index) => (
                  <Chip key={index}>
                    {condition.substring(0, 50)}{condition.length > 50 ? '...' : ''}
                    <ChipRemove type="button" onClick={() => handleRemoveCondition(index)}>
                      <FiX />
                    </ChipRemove>
                  </Chip>
                ))}
              </ChipContainer>

              <div style={{ display: 'flex', gap: theme.spacing.sm, marginTop: theme.spacing.sm }}>
                <TextArea
                  value={newCondition}
                  onChange={(e) => setNewCondition(e.target.value)}
                  placeholder="Ex: Revenus minimum requis : 2550 FCFA nets mensuels..."
                  style={{ minHeight: '60px' }}
                />
                <AddButton type="button" onClick={handleAddCondition}>
                  Ajouter
                </AddButton>
              </div>
            </FormSection>
          </ModalBody>

          <ModalFooter>
            <Button type="button" variant="secondary" onClick={onClose} disabled={submitting}>
              Annuler
            </Button>
            <Button type="submit" variant="primary" disabled={submitting}>
              {submitting ? 'En cours...' : (mode === 'add' ? 'Ajouter' : 'Enregistrer')}
            </Button>
          </ModalFooter>
        </form>
      </ModalContainer>
    </ModalOverlay>
  );
};
